import React, { ReactElement } from 'react';
import {
  Tooltip as MuiTooltip,
  TooltipProps as MuiTooltipProps,
} from '@mui/material';

export type ToolTipTheme = 'dark' | 'light';

export type ToolTipProps = {
  theme?: ToolTipTheme;
  arrowOffset?: number;
} & Omit<MuiTooltipProps, 'children'> & {
    children: ReactElement;
  };

const themeStyles: Record<ToolTipTheme, { background: string; color: string }> =
  {
    dark: {
      background: '#000',
      color: '#FFF',
    },
    light: {
      background: 'var(--dk-bg, #FFF)',
      color: 'var(--dk-text, #000)',
    },
  };

const ToolTip = ({
  theme = 'dark',
  arrowOffset = 16,
  placement = 'top-start',
  arrow = true,
  children,
  slotProps,
  ...props
}: ToolTipProps) => {
  const { background, color } = themeStyles[theme];

  return (
    <MuiTooltip
      placement={placement}
      arrow={arrow}
      slotProps={{
        ...slotProps,
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, -2] } }],
          ...slotProps?.popper,
        },
        tooltip: {
          sx: {
            display: 'flex',
            minWidth: 40,
            maxWidth: 300,
            padding: '4px 8px',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '4px',
            backgroundColor: background,
            color: color,
            fontFamily: 'Pretendard',
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '20px',
            textAlign: 'center',
            wordBreak: 'break-all',
            boxShadow:
              theme === 'light' ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none',
            ...((slotProps?.tooltip as { sx?: object })?.sx || {}),
          },
        },
        arrow: {
          sx: {
            color: background,
            left: `${arrowOffset}px !important`,
            right: 'auto !important',
            transform: 'translateX(0) !important',
            '&::before': {
              borderRadius: 0,
            },
            ...((slotProps?.arrow as { sx?: object })?.sx || {}),
          },
        },
      }}
      {...props}
    >
      {children}
    </MuiTooltip>
  );
};

export default ToolTip;
